import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { usePOCreation } from '../contextProvider/POCreationContext';

const MULTI_SIZE_PACKED = 'multi-size-packed';

/**
 * GrandTotalForPO Component
 * Handles grand total information for Purchase Order creation
 * All controls are enabled for PO creation (product-level override)
 */
export default function GrandTotalForPO({ isPreviewMode = false }) {
  const { t } = useTranslation('purchase-order');
  const { creationState, handlePOFieldChange, currencySymbol } = usePOCreation();

  /**
   * Calculate units for a row based on pack type:
   * - single-packed: quantity
   * - multi-size-packed: ratio * packs (for each sub-row)
   * - other pack types: ratio * packs
   */
  const calculateRowUnits = useCallback((row) => {
    const packType = row?.packType;
    switch (packType) {
      case 'single-packed':
        return Number(row?.quantity || 0);
      case MULTI_SIZE_PACKED:
      case 'multiple-single-size-packed':
      case 'multi-destination-packed':
        return Number(row?.ratio || 0) * Number(row?.packs || 0);
      default:
        return Number(row?.packs || row?.quantity || 0);
    }
  }, []);

  const calculateRowUnitsForTotal = useCallback(
    (row) => {
      return calculateRowUnits(row);
    },
    [calculateRowUnits],
  );

  /**
   * Calculate grand totals:
   * - single-packed: sum of quantity (units), sum of totals (price)
   * - multi-size-packed: sum of (ratio * packs) for all sub-rows (units), sum of totals for all sub-rows (price)
   *   - If no sub-rows exist, calculate ratio * packs for the parent row itself
   * - other pack types: sum of (ratio * packs) (units), sum of totals (price)
   */
  const computedGrandTotals = useMemo(() => {
    const rows = creationState?.orderDetails || [];

    let grandTotalUnits = 0;
    let grandTotalPriceAfterDiscount = 0;

    const processedMultiSizeParents = new Set();

    rows.forEach((row) => {
      const units = calculateRowUnitsForTotal(row);
      const lineTotal = Number(row?.total || 0);

      grandTotalUnits += units;
      grandTotalPriceAfterDiscount += lineTotal;
    });

    const shippingCosts = Number(creationState?.shippingDetails?.shippingCosts || 0);
    const finalGrandTotal = Number(grandTotalPriceAfterDiscount.toFixed(2)) + Number(shippingCosts.toFixed(2));

    return {
      grandTotalUnits,
      grandTotalPriceAfterDiscount: Number(finalGrandTotal.toFixed(2)),
    };
  }, [creationState?.orderDetails, creationState?.shippingDetails?.shippingCosts, calculateRowUnitsForTotal]);

  useEffect(() => {
    const current = creationState?.grandTotal || {};
    if (
      current.grandTotalUnits !== computedGrandTotals.grandTotalUnits ||
      current.grandTotalPriceAfterDiscount !== computedGrandTotals.grandTotalPriceAfterDiscount
    ) {
      handlePOFieldChange('grandTotal', {
        ...current,
        ...computedGrandTotals,
      });
    }
  }, [computedGrandTotals, creationState?.grandTotal, handlePOFieldChange]);

  return (
    <div className="inner-container grand-total-section p-10 mb-8">
      <div className="section-template-title">{t('CREATE_EDIT_PO.GRAND_TOTAL')}</div>
      <div className="d-flex">
        <div className="common-input-control flex-fill mr-16 mb-0">
          <p className="field-name">{t('CREATE_EDIT_PO.GRAND_TOTAL_UNITS')}</p>
          {isPreviewMode ? (
            <div className="form-control-plaintext">{computedGrandTotals.grandTotalUnits ?? '--'}</div>
          ) : (
            <input type="number" name="grandTotalUnits" className="form-control" value={computedGrandTotals.grandTotalUnits} readOnly disabled />
          )}
        </div>

        <div className="common-input-control flex-fill mr-16 mb-0">
          <p className="field-name">
            {t('CREATE_EDIT_PO.GRAND_TOTAL_PRICE_AFTER_DISCOUNT')} ({currencySymbol})
          </p>
          {isPreviewMode ? (
            <div className="form-control-plaintext">{computedGrandTotals.grandTotalPriceAfterDiscount ?? '--'}</div>
          ) : (
            <input
              type="number"
              name="grandTotalPriceAfterDiscount"
              className="form-control"
              value={computedGrandTotals.grandTotalPriceAfterDiscount}
              readOnly
              disabled
            />
          )}
        </div>
      </div>
    </div>
  );
}

GrandTotalForPO.propTypes = {
  isPreviewMode: PropTypes.bool,
};

GrandTotalForPO.defaultProps = {
  isPreviewMode: false,
};
